import React, { FC, FormEvent, useState } from 'react';
import { LoadingAnimation } from '../../../../LoadingAnimation/LoadingAnimation';

import { Input } from '../../../Partials/Input';
import { Textarea } from '../../../Partials/Textarea';
import { InsuranceFormSubmissionStatus } from '../../../Utils/types';
import { FormControls } from '../../FormControls';

import { InsuranceContactFormFields, useInsuranceContactForm } from '../Utils/contactForm';
import { FormSubmittedStatus } from '../../FormSubmittedStatus';
import { useInsuranceLabels } from '../../../Utils/Context/labelsContext';
import { Tooltip } from '../../../Partials/Tooltip';
import { ContactApi } from '../../../../Api/ContactApi';

export const ContactForm: FC = () => {
  const { state, dispatch } = useInsuranceContactForm();
  const [formSubmissionStatus, setFormSubmissionStatus] = useState<InsuranceFormSubmissionStatus>('initial');
  const {
    headings,
    buttons,
    inputs,
    labels
  } = useInsuranceLabels();

  const contactApi = new ContactApi();

  const RequiredLabel = () =>
    <span className="contactForm__required">{inputs.requiredLabel}</span>

  const SubmissionLabel = () =>
    <span>
      {buttons.sendMailButtonLabel}
      <span className="contactForm__loading">
        {formSubmissionStatus === "loading" && (
          <LoadingAnimation className="contactForm__loadingItem" />
        )}
      </span>
    </span>

  const dispatchValue = <T extends {}>(field: InsuranceContactFormFields, value: T) =>
    dispatch({
      field,
      type: 'update',
      payload: value
    })


  const submitForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (formSubmissionStatus === 'loading') return;
    setFormSubmissionStatus('loading')
    try {
      await contactApi.sendMail({
        from: state.from,
        title: state.title,
        message: state.message
      });
      setFormSubmissionStatus('success');
    } catch (error) {
      setFormSubmissionStatus('error');
    }
  }

  const onSubmitClick = () => {
    dispatch({
      field: 'submitted',
      type: 'update',
      payload: true
    })
  }

  return (
    <form
      onSubmit={submitForm}
      className="contactForm__form"
      name="InsuranceContactForm"
    >
      <div className="contactForm__tooltipWrapper">
        <Tooltip
          placement="bottom-start"
          text={false ? headings.helsemailFormTooltip : headings.contactFormTooltip}
          tooltipClassName="contactForm__tooltip"
          anchorClassName="contactForm__anchorTooltip"
        />
      </div>
      <div className="contactForm__formInputs">
        <div className="contactForm__recipient">
          <span className="contactForm__recipientHeader">
            {inputs.recipientLabel}
          </span>
          <a
            className="contactForm__recipientLink"
            href={`mailTo:${inputs.recipientAddress}`}
          >
            {inputs.recipientAddress}
          </a>
        </div>
        <Input<string>
          name="from"
          label={inputs.fromLabel}
          type='email'
          onValueChange={(value) => dispatchValue<string>('from', value)}
          placeholder={inputs.fromPlaceholder}
          backgroundVariant="blue-light"
          error={
            state.submitted && state.from.length === 0
              ? inputs.requiredLabel
              : undefined
          }
          requiredText={<RequiredLabel />}
          required
        />
        <Input<string>
          name="title"
          label={inputs.titleLabel}
          type='text'
          onValueChange={(value) => dispatchValue<string>('title', value)}
          placeholder={inputs.titlePlaceholder}
          backgroundVariant="blue-light"
          error={
            state.submitted && state.title.length === 0
              ? inputs.requiredLabel
              : undefined
          }
          requiredText={<RequiredLabel />}
          required
        />
        {false && (
          <Input<number>
            name="caseNumber"
            label={inputs.caseNumberLabel}
            type='number'
            onValueChange={(value) => dispatchValue<number>('caseNumber', value)}
            placeholder={inputs.caseNumberPlaceholder}
            backgroundVariant="blue-light"
            error={
              state.submitted && !state.caseNumber
                ? inputs.requiredLabel
                : undefined
            }
            requiredText={<RequiredLabel />}
            required
          />
        )}
        <Textarea
          name="message"
          label={inputs.messageLabel}
          onValueChange={(value) => dispatchValue<string>('message', value)}
          placeholder={inputs.caseNumberPlaceholder}
          backgroundVariant="blue-light"
          error={
            state.submitted && state.message.length === 0
              ? inputs.requiredLabel
              : undefined
          }
          requiredText={<RequiredLabel />}
          required
        />
      </div>
      {(formSubmissionStatus === 'initial' || formSubmissionStatus === 'loading') && (
        <FormControls
          onSubmit={onSubmitClick}
          submitButtonLabel={<SubmissionLabel />}
          discardButtonIcon="s-close-with-padding"
          submitButtonIcon="s-arrow-right-dash-with-padding"
          buttonsSize="xlarge"
        />
      )}
      {(formSubmissionStatus === 'success' || formSubmissionStatus === 'error') && (
        <FormSubmittedStatus
          status={formSubmissionStatus}
          successMessage={labels.contactSubmissionSuccess}
          errorMessage={labels.contactSubmissionError}
        />
      )}
    </form>
  )
};
